import {
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { EditGuard } from '@/components/guards/permission-guards';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { API_ENDPOINTS } from '@/config/api-endpoints';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { Role, UserData, UserUpdateFormData } from '@/types/api-responses';
import { RESOURCES } from '@/utils/permissions';
import { handleUserRoleUpdatePermissions } from '@/utils/permissions/refresh';

import {
  getAccessibilityProps,
  getApiErrorMessage,
  getDefaultUserUpdateValues,
  getFieldProps,
  getLoadingStates,
  getSuccessMessages,
  getUserRoleId,
  prepareUpdateUserPayload,
  transformRolesForSelect,
  userValidationRules,
} from './shared/user-form-utils';

interface UserUpdateSheetProps {
  user: UserData;
  onUserUpdated?: () => Promise<void>;
}

const UserUpdateSheet: React.FC<UserUpdateSheetProps> = ({
  user,
  onUserUpdated,
}) => {
  const api = useEnhancedApi();
  const auth = useAuth();
  const { hideSheet, showSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  // State management
  const [roles, setRoles] = useState<Role[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [selectedRoleInfo, setSelectedRoleInfo] = useState<Role | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const hasFetchedRolesRef = useRef(false);

  // Form setup with enhanced defaults
  const { handleSubmit, control, reset, setValue, watch } =
    useForm<UserUpdateFormData>({
      defaultValues: getDefaultUserUpdateValues(user),
      mode: 'onChange', // Enable real-time validation
    });

  // Watch roleId to show role details
  const watchedRoleId = watch('roleId');

  // Update selected role info when roleId changes
  useEffect(() => {
    if (watchedRoleId) {
      const role = roles.find(r => r._id === watchedRoleId);
      setSelectedRoleInfo(role || null);
    } else {
      setSelectedRoleInfo(null);
    }
  }, [watchedRoleId, roles]);

  // Fetch roles on component mount with better error handling
  const fetchRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    setFormErrors([]);

    try {
      const response = await api.get<{ roles: Role[] }>(
        API_ENDPOINTS.RBAC.GET_ROLES
      );

      if (response.success && response.data?.roles) {
        const activeRoles = response.data.roles.filter(
          role => role.isActive !== false
        );
        setRoles(activeRoles);
      } else {
        const errorMessage = getApiErrorMessage(
          response.error,
          'Failed to fetch available roles'
        );
        setFormErrors([errorMessage]);
        showError(errorMessage);
      }
    } catch {
      const errorMessage = 'Unable to connect to server. Please try again.';
      setFormErrors([errorMessage]);
      showError(errorMessage);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [api, showError]);

  useEffect(() => {
    if (!hasFetchedRolesRef.current) {
      hasFetchedRolesRef.current = true;
      fetchRoles();
    }
  }, [fetchRoles]);

  // Initialize role selection when roles or user change
  useEffect(() => {
    if (!user || roles.length === 0) return;
    const userRoleId = getUserRoleId(user, roles);
    if (userRoleId) {
      setValue('roleId', userRoleId);
      const role = roles.find(r => r._id === userRoleId) || null;
      setSelectedRoleInfo(role);
    }
  }, [user, roles, setValue]);

  // Enhanced form submission with better error handling
  const handleUpdateUser = handleSubmit(
    async (formData: UserUpdateFormData) => {
      if (!user?._id) {
        const errorMessage =
          'User information is missing. Please refresh and try again.';
        setFormErrors([errorMessage]);
        showError(errorMessage);
        return;
      }

      setIsSubmitting(true);
      setFormErrors([]);

      try {
        const payload = prepareUpdateUserPayload(formData);
        const updateUrl = `${API_ENDPOINTS.USERS.UPDATE(user._id)}`;

        const response = await api.put(updateUrl, payload);

        if (response.success) {
          const displayName =
            formData.fullname || formData.username || user.email;
          const successMessage = getSuccessMessages.userUpdated(displayName);
          showSuccess(successMessage);
          hideSheet('edit-user');

          // Handle permission refresh after user role update
          await handleUserRoleUpdatePermissions(user._id, auth, {
            showToast: false,
            notifyOtherUsers: true,
          });

          // Refresh the users list
          if (onUserUpdated) {
            await onUserUpdated();
          }
        } else {
          const errorMessage = getApiErrorMessage(
            response.error,
            'Failed to update user information'
          );
          setFormErrors([errorMessage]);
          showError(errorMessage);
        }
      } catch {
        const errorMessage =
          'Unable to update user. Please check your connection and try again.';
        setFormErrors([errorMessage]);
        showError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  );

  // Enhanced delete function with better error handling
  const handleDeleteUser = useCallback(async () => {
    if (!user?._id) {
      showError('User information is missing');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.delete(
        `${API_ENDPOINTS.USERS.DELETE(user._id)}`
      );

      if (response.success) {
        const displayName = user.fullname || user.username || user.email;
        const successMessage = getSuccessMessages.userDeleted(displayName);
        showSuccess(successMessage);
        hideSheet('edit-user');
        hideSheet('delete-user-confirm');

        // Refresh the users list
        if (onUserUpdated) {
          await onUserUpdated();
        }
      } else {
        const errorMessage = getApiErrorMessage(
          response.error,
          'Failed to delete user'
        );
        showError(errorMessage);
      }
    } catch {
      showError('Unable to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [user, api, showSuccess, showError, hideSheet, onUserUpdated]);

  const handleCancel = useCallback(() => {
    reset();
    setFormErrors([]);
    setSelectedRoleInfo(null);
    hideSheet('edit-user');
  }, [reset, hideSheet]);

  const handleRetryRoles = useCallback(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Enhanced delete confirmation dialog
  const showDeleteConfirmation = useCallback(() => {
    showSheet({
      id: 'delete-user-confirm',
      type: 'confirmation',
      title: 'Delete User Account',
      content: (
        <div className='py-4'>
          <div className='flex gap-4 items-start mb-4'>
            <WarningIcon
              size={24}
              className='text-destructive-50 mt-1 flex-shrink-0'
            />
            <div className='flex-1'>
              <h3 className='font-semibold text-gray-90 text-base mb-2'>
                Permanently Delete User
              </h3>
              <div className='space-y-2 text-sm text-gray-60'>
                <p>
                  You&rsquo;re about to delete{' '}
                  <strong>
                    {user?.fullname || user?.username || user?.email}
                  </strong>{' '}
                  ({user?.email}).
                </p>
                <p className='text-destructive-70 font-medium'>
                  This action cannot be undone and will:
                </p>
                <ul className='text-destructive-60 text-xs space-y-1 ml-4'>
                  <li>• Remove all user access immediately</li>
                  <li>• Delete user profile and settings</li>
                  <li>• Invalidate all active sessions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
      buttons: [
        {
          id: 'delete',
          label: 'Yes, Delete User',
          onClick: handleDeleteUser,
          color: 'destructive',
          hierarchy: 'primary',
          loading: isDeleting,
          disabled: isDeleting,
        },
        {
          id: 'cancel',
          label: 'Keep User',
          onClick: () => hideSheet('delete-user-confirm'),
          color: 'gray',
          hierarchy: 'secondary',
        },
      ],
    });
  }, [showSheet, user, handleDeleteUser, isDeleting, hideSheet]);

  // Expose handlers for dynamic button system
  React.useEffect(() => {
    const loadingStates = getLoadingStates(
      isSubmitting,
      isLoadingRoles,
      isDeleting
    );

    (
      window as typeof window & {
        userUpdateHandlers?: {
          handleUpdateUser: () => void;
          handleCancel: () => void;
          showDeleteConfirmation: () => void;
          isLoading: boolean;
        };
      }
    ).userUpdateHandlers = {
      handleUpdateUser,
      handleCancel,
      showDeleteConfirmation,
      isLoading: loadingStates.isSubmitDisabled,
    };

    return () => {
      delete (window as typeof window & { userUpdateHandlers?: unknown })
        .userUpdateHandlers;
    };
  }, [
    handleUpdateUser,
    handleCancel,
    showDeleteConfirmation,
    isSubmitting,
    isLoadingRoles,
    isDeleting,
  ]);

  // Get loading states
  const loadingStates = getLoadingStates(
    isSubmitting,
    isLoadingRoles,
    isDeleting
  );

  // Early return if user is null
  if (!user) {
    return (
      <div className='p-6 text-center'>
        <WarningIcon className='h-12 w-12 text-gray-40 mx-auto mb-4' />
        <h3 className='font-medium text-gray-90 mb-2'>No User Selected</h3>
        <p className='text-gray-50 text-sm'>
          Please select a user to edit their information.
        </p>
      </div>
    );
  }

  // Show error state if roles failed to load
  if (formErrors.length > 0 && roles.length === 0) {
    return (
      <div className='space-y-4 p-4'>
        <div className='flex items-center gap-3 p-4 bg-destructive-5 border border-destructive-20 rounded-lg'>
          <WarningIcon className='h-5 w-5 text-destructive-50 flex-shrink-0' />
          <div className='flex-1'>
            <h3 className='font-medium text-destructive-90'>
              Unable to Load User Roles
            </h3>
            <div className='mt-1 text-sm text-destructive-70'>
              {formErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </div>
        </div>
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={handleRetryRoles}
            disabled={isLoadingRoles}
            className='px-4 py-2 text-sm font-medium text-destructive-70 bg-white border border-destructive-30 rounded-md hover:bg-destructive-5 disabled:opacity-50'
          >
            {isLoadingRoles ? 'Retrying...' : 'Retry Loading Roles'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <EditGuard resource={RESOURCES.USERS}>
      <div className='space-y-6'>
        {/* User info header */}
        <div className='flex items-center gap-3 p-4 bg-brand-5 border border-brand-20 rounded-lg'>
          <UserIcon className='h-6 w-6 text-brand-60 flex-shrink-0' />
          <div className='flex-1 min-w-0'>
            <h3 className='font-medium text-brand-90 truncate'>
              Editing: {user.fullname || user.username || user.email}
            </h3>
            <p className='text-sm text-brand-70 truncate'>{user.email}</p>
          </div>
          <Badge
            variant={user.isActive ? 'default' : 'secondary'}
            className='flex-shrink-0'
          >
            {user.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        {/* Form errors display */}
        {formErrors.length > 0 && (
          <div className='p-4 bg-destructive-5 border border-destructive-20 rounded-lg'>
            <div className='flex items-start gap-3'>
              <WarningIcon className='h-5 w-5 text-destructive-50 flex-shrink-0 mt-0.5' />
              <div className='flex-1'>
                <h3 className='font-medium text-destructive-90 text-sm'>
                  Please fix the following errors:
                </h3>
                <ul className='mt-1 text-sm text-destructive-70 space-y-1'>
                  {formErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleUpdateUser} className='space-y-4'>
          {/* Email field */}
          <Controller
            name='email'
            control={control}
            rules={userValidationRules.email}
            render={({ field, fieldState }) => {
              const fieldProps = getFieldProps(
                field,
                fieldState,
                loadingStates.isFormDisabled
              );
              const accessibilityProps = getAccessibilityProps(
                'email',
                !!fieldState?.error
              );

              return (
                <div className='relative'>
                  <EnvelopeIcon className='absolute left-3 top-9 h-4 w-4 text-gray-40' />
                  <Input
                    {...fieldProps}
                    {...accessibilityProps}
                    label='Email Address'
                    type='email'
                    placeholder='user@company.com'
                    className='w-full pl-10'
                    autoComplete='email'
                  />
                </div>
              );
            }}
          />

          {/* Full name field (required) */}
          <Controller
            name='fullname'
            control={control}
            rules={{
              ...userValidationRules.fullname,
              required: 'Full name is required',
            }}
            render={({ field, fieldState }) => {
              const fieldProps = getFieldProps(
                field,
                fieldState,
                loadingStates.isFormDisabled
              );
              const accessibilityProps = getAccessibilityProps(
                'fullname',
                !!fieldState?.error
              );

              return (
                <div className='relative'>
                  <UserIcon className='absolute left-3 top-9 h-4 w-4 text-gray-40' />
                  <Input
                    {...fieldProps}
                    {...accessibilityProps}
                    label='Full Name'
                    placeholder='John Doe'
                    className='w-full pl-10'
                    autoComplete='name'
                  />
                </div>
              );
            }}
          />

          {/* Phone field (optional) */}
          <Controller
            name='phone'
            control={control}
            rules={userValidationRules.phone}
            render={({ field, fieldState }) => {
              const fieldProps = getFieldProps(
                field,
                fieldState,
                loadingStates.isFormDisabled
              );
              const accessibilityProps = getAccessibilityProps(
                'phone',
                !!fieldState?.error
              );

              return (
                <div className='relative'>
                  <PhoneIcon className='absolute left-3 top-9 h-4 w-4 text-gray-40' />
                  <Input
                    {...fieldProps}
                    {...accessibilityProps}
                    label='Phone Number (Optional)'
                    placeholder='+1 (555) 123-4567'
                    className='w-full pl-10'
                    autoComplete='tel'
                  />
                </div>
              );
            }}
          />

          {/* Role selection field */}
          <Controller
            name='roleId'
            control={control}
            rules={userValidationRules.roleId}
            render={({ field, fieldState }) => {
              const accessibilityProps = getAccessibilityProps(
                'roleId',
                !!fieldState?.error
              );

              return (
                <div className='space-y-2'>
                  <FilterSelect
                    {...accessibilityProps}
                    name='roleId'
                    options={transformRolesForSelect(roles)}
                    className='w-full'
                    label='Role'
                    error={fieldState?.error?.message}
                    onChange={field.onChange}
                    value={field.value}
                    placeholder='Select a role for this user'
                    disabled={
                      loadingStates.isFormDisabled || roles.length === 0
                    }
                  />

                  {/* Show role information when selected */}
                  {selectedRoleInfo && (
                    <Card className='p-3 bg-brand-5 border-brand-20'>
                      <div className='flex items-start gap-3'>
                        <UserIcon className='h-4 w-4 text-brand-60 mt-0.5 flex-shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className='font-medium text-brand-90 text-sm'>
                              {selectedRoleInfo.name}
                            </span>
                            <Badge variant='outline' className='text-xs'>
                              {selectedRoleInfo.permissions?.length || 0}{' '}
                              permissions
                            </Badge>
                          </div>
                          {selectedRoleInfo.description && (
                            <p className='text-xs text-brand-70 mt-1'>
                              {selectedRoleInfo.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              );
            }}
          />

          {/* Account status indicator */}
          <div className='pt-4 border-t border-gray-20'>
            <div className='flex items-start gap-3 p-3 bg-gray-5 rounded-lg'>
              <CheckCircleIcon className='h-4 w-4 text-gray-5 mt-0.5 flex-shrink-0' />
              <div className='text-sm text-gray-60'>
                <p className='font-medium mb-1'>Account Status Information:</p>
                <div className='text-xs space-y-1'>
                  <div className='flex items-center gap-2'>
                    <span>Status:</span>
                    <Badge
                      variant={user.isActive ? 'default' : 'secondary'}
                      className='text-xs'
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div>
                    Created: {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                  {user.lastLogin && (
                    <div>
                      Last login:{' '}
                      {new Date(user.lastLogin).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </EditGuard>
  );
};

export default UserUpdateSheet;
